/**
 * kaytekSpeedtestFixedBudgetRun()
 *
 * Always performs a single-socket download + upload measurement every call.
 * Each call uses a fixed bytes/run computed so that:
 *   expectedRunsPerDay * budgetDaysThisMonth * bytesPerRunTotal <= monthlyBudgetBytes
 *
 * It does NOT skip, does NOT adapt sizes to network, does NOT enforce caps.
 * It DOES track (observability only):
 *   - trackingBeganIso
 *   - monthStartIso
 *   - bytesUsedThisMonthObserved
 *
 * Endpoints:
 *   GET  {baseUrl}/download?size=BYTES
 *   POST {baseUrl}/upload
 */
export async function kaytekSpeedtestFixedBudgetRun(opts = {}) {
  const {
    baseUrl = '/api/speedtest',

    monthlyBudgetBytes = 100 * 1024 * 1024, /* Don't change here -- change in SAGA */
    expectedRunsPerDay = 48, /* Don't change here -- change in SAGA */
    numMidburnDaysInMonth = null, /* Don't change here -- change in SAGA */

    // Split of bytes/run between download and upload
    downShare = 0.3, // 30% down, 70% up

    // Hard minimums (only used if the computed per-run total is larger)
    minDownloadBytes = 10_000,
    minUploadBytes = 10_000,

    // Storage key for tracking
    storageKey = 'kaytek_speedtest_fixedbudget_v1',

    // Execution details
    cacheBust = true,
    maxDownloadSec = 20,
    maxUploadSec = 25
  } = opts;

  const now = new Date();
  const nowMs = () => (performance?.now ? performance.now() : Date.now());
  const iso = (d) => d.toISOString();

  function monthStartUtc(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0));
  }
  function nextMonthStartUtc(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1, 0, 0, 0));
  }
  function daysInMonthUtc(d) {
    const start = monthStartUtc(d);
    const next = nextMonthStartUtc(d);
    return Math.round((next.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  }

  function safeParse(json) {
    try { return JSON.parse(json); } catch { return null; }
  }

  function loadState() {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? safeParse(raw) : null;

    const mStartIso = iso(monthStartUtc(now));
    const trackingBeganIso = parsed?.trackingBeganIso || iso(now);

    const sameMonth = parsed?.monthStartIso === mStartIso;
    const bytesUsedThisMonthObserved = sameMonth ? (Number(parsed?.bytesUsedThisMonthObserved) || 0) : 0;

    return { trackingBeganIso, monthStartIso: mStartIso, bytesUsedThisMonthObserved };
  }

  function saveState(state) {
    localStorage.setItem(storageKey, JSON.stringify(state));
  }

  function mbpsFrom(bytes, ms) {
    if (!ms || ms <= 0) return 0;
    return ((bytes * 8) / (ms / 1000)) / 1_000_000;
  }

  // ---- Fixed bytes/run for THIS month length ----
  const dim = daysInMonthUtc(now);
  const budgetDaysThisMonth = Number(numMidburnDaysInMonth || dim);
  const plannedRunsThisMonth = expectedRunsPerDay * budgetDaysThisMonth;

// This is the whole point: fixed bytes per run so that expectedRunsPerDay over budgetDaysThisMonth sums to the traffic budget.

  const bytesPerRunTotal = Math.floor(monthlyBudgetBytes / plannedRunsThisMonth);

  // Split between download/upload (with optional minimum bumps only if total allows)
  let downloadBytes = Math.floor(bytesPerRunTotal * downShare);
  let uploadBytes = bytesPerRunTotal - downloadBytes;

  const minSum = minDownloadBytes + minUploadBytes;
  const belowRecommendedMinimums = bytesPerRunTotal < minSum;

  if (!belowRecommendedMinimums) {
    // Ensure each direction has its minimum while keeping total fixed.
    if (downloadBytes < minDownloadBytes) {
      downloadBytes = minDownloadBytes;
      uploadBytes = bytesPerRunTotal - downloadBytes;
    }
    if (uploadBytes < minUploadBytes) {
      uploadBytes = minUploadBytes;
      downloadBytes = bytesPerRunTotal - uploadBytes;
    }
  }

  // ---- Execution helpers (single socket each) ----
  async function runDownload(sizeBytes) {
    const controller = new AbortController();
    const t0 = nowMs();

    const qs = new URLSearchParams({ size: String(sizeBytes) });
    if (cacheBust) qs.set('t', String(Date.now()));

    const timeout = setTimeout(() => controller.abort(), Math.floor(maxDownloadSec * 1000));

    try {
      const res = await fetch(`${baseUrl}/download?${qs.toString()}`, {
        cache: 'no-store',
        signal: controller.signal
      });
      if (!res.ok) {
        return { ok: false, error: { name: 'HTTPError', message: `Download HTTP ${res.status}` }, bytes: 0, ms: nowMs() - t0, mbps: 0 };
      }

      let bytes = 0;
      if (res.body?.getReader) {
        const reader = res.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          bytes += value.byteLength;
        }
      } else {
        bytes = (await res.arrayBuffer()).byteLength;
      }

      const ms = nowMs() - t0;
      return { ok: true, bytes, ms, mbps: mbpsFrom(bytes, ms) };
    } catch (e) {
      const ms = nowMs() - t0;
      const name = e?.name || 'Error';
      const msg = e?.message || String(e);
      return { ok: false, error: { name, message: msg }, bytes: 0, ms, mbps: 0, aborted: name === 'AbortError' };
    } finally {
      clearTimeout(timeout);
    }
  }

  function makePatternPayload(size) {
    // Low CPU. Assumes you keep gzip off for /api/speedtest/ (as you already configured).
    const u8 = new Uint8Array(size);
    for (let i = 0; i < u8.length; i++) u8[i] = i & 0xff;
    return u8;
  }

  function runUpload(sizeBytes) {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const url = `${baseUrl}/upload${cacheBust ? `?t=${Date.now()}` : ''}`;
      const payload = makePatternPayload(sizeBytes);

      let start = 0;
      let loadedFinal = 0;

      const timer = setTimeout(() => { try { xhr.abort(); } catch (_) {} }, Math.floor(maxUploadSec * 1000));

      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.responseType = 'text';

      xhr.upload.onprogress = (e) => { loadedFinal = e.loaded || loadedFinal; };

      xhr.onload = () => {
        clearTimeout(timer);
        const ms = nowMs() - start;
        const bytes = loadedFinal || sizeBytes;
        resolve({ ok: true, bytes, ms, mbps: mbpsFrom(bytes, ms), status: xhr.status });
      };

      xhr.onerror = () => {
        clearTimeout(timer);
        const ms = nowMs() - start;
        resolve({ ok: false, error: { name: 'NetworkError', message: 'Upload XHR network error' }, bytes: loadedFinal, ms, mbps: loadedFinal ? mbpsFrom(loadedFinal, ms) : 0, status: xhr.status });
      };

      xhr.onabort = () => {
        clearTimeout(timer);
        const ms = nowMs() - start;
        resolve({ ok: false, error: { name: 'AbortError', message: 'Upload aborted (timeout)' }, bytes: loadedFinal, ms, mbps: loadedFinal ? mbpsFrom(loadedFinal, ms) : 0, aborted: true, status: xhr.status });
      };

      start = nowMs();
      xhr.send(payload);
    });
  }

  // ---- Tracking (observability only) ----
  const state = loadState();

  const out = {
    ok: true,
    meta: {
      baseUrl,
      storageKey,
      startedAtIso: iso(new Date())
    },
    budgetModel: {
      monthlyBudgetBytes,
      expectedRunsPerDay,
      daysInThisMonth: dim,
      plannedRunsThisMonth,
      bytesPerRunTotal,
      downShare,
      belowRecommendedMinimums,

      numMidburnDaysInMonth,
      budgetDaysThisMonth,

    },
    runConfig: {
      downloadBytesPlanned: downloadBytes,
      uploadBytesPlanned: uploadBytes,
      minDownloadBytes,
      minUploadBytes
    },
    tracking: {
      trackingBeganIso: state.trackingBeganIso,
      monthStartIso: state.monthStartIso,
      bytesUsedThisMonthObservedBefore: state.bytesUsedThisMonthObserved,
      bytesUsedThisMonthObservedAfter: null
    },
    measured: {
      download: null,
      upload: null
    },
    traffic: {
      bytesThisRunPlanned: bytesPerRunTotal,
      bytesThisRunMeasured: 0
    }
  };

  // Always measure
  out.measured.download = await runDownload(downloadBytes);
  out.measured.upload = await runUpload(uploadBytes);

  const bytesMeasured = (out.measured.download?.bytes || 0) + (out.measured.upload?.bytes || 0);

  // Update observed usage (still not enforcing anything)
  state.bytesUsedThisMonthObserved += bytesMeasured;
  saveState(state);

  out.traffic.bytesThisRunMeasured = bytesMeasured;
  out.tracking.bytesUsedThisMonthObservedAfter = state.bytesUsedThisMonthObserved;
  out.meta.endedAtIso = iso(new Date());

  // overall ok if both succeeded
  out.ok = !!(out.measured.download?.ok && out.measured.upload?.ok);

  return out;
}
export const bytesToGB = (bytes) => {
  const n = Number(bytes || 0)
  return Math.round((n / 1_000_000_000) * 100) / 100
}
